import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { TradeOrder } from '../types';

/** Relative age, never expressed in seconds. */
function relativeAge(timestamp: Date): string {
  const diff = Date.now() - timestamp.getTime();
  if (diff < 60_000) return 'Just now';
  return `${formatDistanceToNowStrict(timestamp, {
    unit: diff < 3_600_000 ? 'minute' : undefined
  })} ago`;
}
const statusStyles: Record<TradeOrder['status'], string> = {
  Confirmed: 'text-gold-light border-gold/40 bg-gold/10',
  'Assay pending': 'text-amber-200 border-amber-300/30 bg-amber-300/10',
  'In transit': 'text-sky-200 border-sky-300/30 bg-sky-300/10',
  'Cleared customs': 'text-violet-200 border-violet-300/30 bg-violet-300/10',
  Delivered: 'text-emerald-200 border-emerald-300/30 bg-emerald-300/10'
};
interface Props {
  orders: TradeOrder[];
  latestId: string | null;
  limit?: number;
}
export function LiveOrderFeed({
  orders,
  latestId,
  limit = 8
}: Props) {
  const visible = orders.slice(0, limit);
  return <div className="overflow-hidden rounded-sm border border-hair bg-panel" data-id="element-142" data-mp="2y6apjym7zm7v">
      <div className="flex items-center justify-between border-b border-hair px-5 py-4" data-id="element-143" data-mp="2y47p0knfyaow">
        <div className="flex items-center gap-3" data-id="element-144" data-mp="32zdlueidt9rr">
          <span className="relative flex h-2 w-2" aria-hidden="true" data-id="element-145" data-mp="37wn9ppq4hddh">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-70" data-id="element-146" data-mp="2a8lk71ijrsx1" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-gold" data-id="element-147" data-mp="2f76iuwi5i4jd" />
          </span>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.22em] text-bone/80" data-id="element-148" data-mp="2k71ch7tb9884">
            Live order book
          </h3>
        </div>
        <p className="font-mono text-[11px] text-muted" data-id="element-149" data-mp="2o9xx142eveep">
          {orders.length} logged · updating
        </p>
      </div>

      <ul className="divide-y divide-hair/70" data-id="element-150" data-mp="3voar9h1hvc33">
        <AnimatePresence initial={false} data-id="element-151" data-mp="3h47rbxng0mw">
          {visible.map(order => <motion.li key={order.id} layout initial={{
          opacity: 0,
          y: -12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} className={`px-5 py-4 ${order.id === latestId ? 'bg-gold/[0.06]' : ''}`} data-id="element-152" data-mp="20ia29nfftwz8">
              <div className="flex items-start justify-between gap-4" data-id="element-153" data-mp="24ik4qq40u59c">
                <div className="min-w-0" data-id="element-154" data-mp="1gv2cktguh55u">
                  <p className="truncate text-sm font-semibold text-bone" data-id="element-155" data-mp="1cth4dlf99f1z">{order.commodity}</p>
                  <p className="mt-1 font-mono text-[11px] text-muted" data-id="element-156" data-mp="1qkc4o9bounty">
                    {order.id} · {order.destination} · {order.incoterm}
                  </p>
                </div>
                <div className="shrink-0 text-right" data-id="element-157" data-mp="y4b6q1w2fww7">
                  <p className="font-mono text-sm text-gold-light" data-id="element-158" data-mp="y300zwj28f2g">{order.quantity}</p>
                  <p className="mt-1 font-mono text-[11px] text-muted" data-id="element-159" data-mp="t46puar8x05g">
                    {format(order.timestamp, 'dd MMM yyyy · HH:mm')}
                  </p>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-3" data-id="element-160" data-mp="39kbm7r86hof9">
                <span className={`rounded-sm border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] ${statusStyles[order.status]}`} data-id="element-161" data-mp="2vs58scwetvji">
                  {order.status}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted" data-id="element-162" data-mp="3ohvpmtpffbjb">
                  {relativeAge(order.timestamp)}
                </span>
              </div>
            </motion.li>)}
        </AnimatePresence>
      </ul>
    </div>;
}